import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, LogOut, ArrowLeft, Plus, Edit2, Trash2, FileText, Loader2, MapPin, Banknote, Clock, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

import { BACKEND_URL } from '../config/api';

const EXPERIENCE_LABELS = {
  IMPROVER: 'Improver',
  SKILLED: 'Skilled Tradesperson',
  SUPERVISOR: 'Supervisor/Lead'
};

export default function AdminOfferTemplates() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    locationPostcode: '',
    locationTown: '',
    rateType: 'DAY',
    dayRateGBP: '',
    hoursStart: '08:00',
    hoursEnd: '16:30',
    breaks: 'UNPAID',
    experienceLevel: '',
    tasksNote: '',
    extras: {
      cscsRequired: false,
      cscsLevel: null,
      tools: null,
      parkingAvailable: false,
      cisAvailable: false,
      paymentTerms: 'WEEKLY'
    }
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      
      if (response.data.user_type !== 'admin') {
        navigate('/login');
        return;
      }
      
      setUser(response.data);
      fetchTemplates();
    } catch (error) {
      navigate('/login');
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/offer-templates`, {
        withCredentials: true
      });
      setTemplates(response.data.templates || []);
    } catch (error) {
      toast.error('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      locationPostcode: '',
      locationTown: '',
      rateType: 'DAY',
      dayRateGBP: '',
      hoursStart: '08:00',
      hoursEnd: '16:30',
      breaks: 'UNPAID',
      experienceLevel: '',
      tasksNote: '',
      extras: {
        cscsRequired: false,
        cscsLevel: null,
        tools: null,
        parkingAvailable: false,
        cisAvailable: false,
        paymentTerms: 'WEEKLY'
      }
    });
    setEditingTemplate(null);
  };

  const handleEdit = (template) => {
    const data = template.template_data || {};
    setFormData({
      name: template.name || data.name || '',
      locationPostcode: data.locationPostcode || '',
      locationTown: data.locationTown || '',
      rateType: data.rateType || 'DAY',
      dayRateGBP: data.dayRateGBP || '',
      hoursStart: data.hoursStart || '08:00',
      hoursEnd: data.hoursEnd || '16:30',
      breaks: data.breaks || 'UNPAID',
      experienceLevel: data.experienceLevel || '',
      tasksNote: data.tasksNote || '',
      extras: data.extras || {
        cscsRequired: false,
        cscsLevel: null,
        tools: null,
        parkingAvailable: false,
        cisAvailable: false,
        paymentTerms: 'WEEKLY'
      }
    });
    setEditingTemplate(template);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Template name is required');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        template: {
          ...formData,
          dayRateGBP: formData.dayRateGBP ? parseFloat(formData.dayRateGBP) : null
        },
        is_global: true
      };
      
      if (editingTemplate) {
        await axios.put(
          `${BACKEND_URL}/api/offer-templates/${editingTemplate.template_id}`,
          payload,
          { withCredentials: true }
        );
        toast.success('Template updated');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/offer-templates`,
          payload,
          { withCredentials: true }
        );
        toast.success('Template created');
      }
      
      setShowForm(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/offer-templates/${templateId}`, {
        withCredentials: true
      });
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  const handleExtrasChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      extras: { ...prev.extras, [field]: value }
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const globalTemplates = templates.filter(t => t.is_global);
  const userTemplates = templates.filter(t => !t.is_global);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-offer-templates-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <Building2 className="h-6 w-6 text-orange-500" />
              <h1 className="text-xl font-bold text-slate-900">Offer Templates</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">Hi, {user?.name}</span>
            <button
              onClick={handleLogout}
              className="btn-secondary text-sm flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Global Templates Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Global Templates</h2>
              <p className="text-sm text-slate-500">Available to all employers when creating offers</p>
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="btn-primary flex items-center gap-2"
              data-testid="add-template-btn"
            >
              <Plus className="h-4 w-4" />
              Add Template
            </button>
          </div>
          
          {globalTemplates.length === 0 ? (
            <div className="card text-center py-12">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No global templates yet</p>
              <p className="text-sm text-slate-400 mt-1">Create templates that employers can use as starting points</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {globalTemplates.map(template => (
                <div
                  key={template.template_id}
                  className="card hover:border-orange-300 transition-colors"
                  data-testid={`template-${template.template_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-slate-900">{template.name}</h3>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleEdit(template)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        data-testid={`edit-${template.template_id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(template.template_id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        data-testid={`delete-${template.template_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    {template.template_data?.locationPostcode && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <MapPin className="h-4 w-4 text-orange-500" />
                        <span>{template.template_data.locationPostcode}</span>
                        {template.template_data.locationTown && (
                          <span>, {template.template_data.locationTown}</span>
                        )}
                      </div>
                    )}
                    {template.template_data?.dayRateGBP && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Banknote className="h-4 w-4 text-orange-500" />
                        <span>£{template.template_data.dayRateGBP}/day</span>
                      </div>
                    )}
                    {template.template_data?.hoursStart && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Clock className="h-4 w-4 text-orange-500" />
                        <span>{template.template_data.hoursStart} - {template.template_data.hoursEnd}</span>
                      </div>
                    )}
                    {template.template_data?.experienceLevel && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <User className="h-4 w-4 text-orange-500" />
                        <span>{EXPERIENCE_LABELS[template.template_data.experienceLevel] || template.template_data.experienceLevel}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Extras chips */}
                  <div className="flex flex-wrap gap-1 mt-3">
                    {template.template_data?.extras?.cscsRequired && (
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">CSCS</span>
                    )}
                    {template.template_data?.extras?.parkingAvailable && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Parking</span>
                    )}
                    {template.template_data?.extras?.cisAvailable && (
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs rounded-full">CIS</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* User Templates Section */}
        {userTemplates.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-slate-900 mb-2">Employer Templates</h2>
            <p className="text-sm text-slate-500 mb-4">Personal templates created by employers</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {userTemplates.map(template => (
                <div
                  key={template.template_id}
                  className="card border-slate-200"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-slate-900">{template.name}</h3>
                    <span className="text-xs text-slate-400">
                      {template.owner?.name || 'Unknown'}
                    </span>
                  </div>
                  
                  <div className="space-y-1 text-sm text-slate-600">
                    {template.template_data?.locationPostcode && (
                      <p>{template.template_data.locationPostcode}</p>
                    )}
                    {template.template_data?.dayRateGBP && (
                      <p>£{template.template_data.dayRateGBP}/day</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Template Modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-orange-500" />
              {editingTemplate ? 'Edit Template' : 'Create Global Template'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="template-form">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Template Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Standard Day Rate - Central London"
                className="w-full"
                required
                data-testid="template-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Postcode</label>
                <input
                  type="text"
                  value={formData.locationPostcode}
                  onChange={(e) => setFormData(prev => ({ ...prev, locationPostcode: e.target.value.toUpperCase() }))}
                  placeholder="e.g., SW1A"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Town/City</label>
                <input
                  type="text"
                  value={formData.locationTown}
                  onChange={(e) => setFormData(prev => ({ ...prev, locationTown: e.target.value }))}
                  placeholder="e.g., London"
                  className="w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Rate Type</label>
                <select
                  value={formData.rateType}
                  onChange={(e) => setFormData(prev => ({ ...prev, rateType: e.target.value }))}
                  className="w-full"
                >
                  <option value="DAY">Fixed Day Rate</option>
                  <option value="OPEN_TO_OFFERS">Open to Offers</option>
                </select>
              </div>
              {formData.rateType === 'DAY' && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Day Rate (£)</label>
                  <input
                    type="number"
                    value={formData.dayRateGBP}
                    onChange={(e) => setFormData(prev => ({ ...prev, dayRateGBP: e.target.value }))}
                    placeholder="e.g., 200"
                    min="120"
                    step="5"
                    className="w-full"
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Start Time</label>
                <input
                  type="time"
                  value={formData.hoursStart}
                  onChange={(e) => setFormData(prev => ({ ...prev, hoursStart: e.target.value }))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">End Time</label>
                <input
                  type="time"
                  value={formData.hoursEnd}
                  onChange={(e) => setFormData(prev => ({ ...prev, hoursEnd: e.target.value }))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Breaks</label>
                <select
                  value={formData.breaks}
                  onChange={(e) => setFormData(prev => ({ ...prev, breaks: e.target.value }))}
                  className="w-full"
                >
                  <option value="PAID">Paid</option>
                  <option value="UNPAID">Unpaid</option>
                  <option value="NA">N/A</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Experience Level</label>
              <select
                value={formData.experienceLevel}
                onChange={(e) => setFormData(prev => ({ ...prev, experienceLevel: e.target.value }))}
                className="w-full"
              >
                <option value="">Not specified</option>
                <option value="IMPROVER">Improver</option>
                <option value="SKILLED">Skilled Tradesperson</option>
                <option value="SUPERVISOR">Supervisor/Lead</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Default Tasks Note</label>
              <textarea
                value={formData.tasksNote}
                onChange={(e) => setFormData(prev => ({ ...prev, tasksNote: e.target.value.slice(0, 200) }))}
                placeholder="Brief description of typical work..."
                rows={2}
                maxLength={200}
                className="w-full text-sm"
              />
            </div>

            {/* Extras */}
            <div className="border-t border-slate-200 pt-4">
              <h4 className="text-sm font-medium text-slate-700 mb-3">Default Extras</h4>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.extras.cscsRequired}
                    onChange={(e) => handleExtrasChange('cscsRequired', e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  <span className="text-sm text-slate-700">CSCS Required</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.extras.parkingAvailable}
                    onChange={(e) => handleExtrasChange('parkingAvailable', e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  <span className="text-sm text-slate-700">Parking Available</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.extras.cisAvailable}
                    onChange={(e) => handleExtrasChange('cisAvailable', e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  <span className="text-sm text-slate-700">CIS Available</span>
                </label>
                <div>
                  <label className="block text-sm text-slate-700 mb-1">Payment Terms</label>
                  <select
                    value={formData.extras.paymentTerms}
                    onChange={(e) => handleExtrasChange('paymentTerms', e.target.value)}
                    className="w-full text-sm"
                  >
                    <option value="SAME_DAY">Same Day</option>
                    <option value="WEEKLY">Weekly</option>
                    <option value="14_DAYS">14 Days</option>
                    <option value="30_DAYS">30 Days</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  resetForm();
                }}
                className="btn-secondary flex-1"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                disabled={saving}
                data-testid="save-template-btn"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  editingTemplate ? 'Update Template' : 'Create Template'
                )}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
